import { useEffect, useState, useCallback } from 'react'
import { apiAgentGetDashboard, ApiRequestError, type AdminDashboardStats } from '@/lib/api'
import { formatCurrency } from '@/lib/utils'
import { Skeleton } from '@/components/ui/skeleton'
import { Button } from '@/components/ui/button'
import { AlertCircle, RefreshCw, Users, Layers, TrendingDown, TrendingUp, Circle, Wrench } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useT } from '@/contexts/language'

export default function AgentDashboardPage() {
  const t = useT()
  const [data, setData] = useState<(AdminDashboardStats & { maintenanceMode?: boolean }) | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      setData(await apiAgentGetDashboard())
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : t.dashboard.statsError)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { void load() }, [load])

  const houseEdge = data
    ? (parseFloat(data.totalBetAmountToday) - parseFloat(data.totalWinAmountToday)).toFixed(2)
    : '0'

  const cards = data
    ? [
        {
          label: t.dashboard.totalMembers,
          value: data.totalMembers.toLocaleString(),
          sub: `${data.activeMembers.toLocaleString()} ${t.dashboard.activeMembers.toLowerCase()}`,
          icon: Users,
        },
        {
          label: t.dashboard.betsToday,
          value: data.totalBetsToday.toLocaleString(),
          sub: '',
          icon: Layers,
        },
        {
          label: t.dashboard.betVolume,
          value: formatCurrency(data.totalBetAmountToday),
          sub: '',
          icon: TrendingUp,
        },
        {
          label: t.dashboard.payouts,
          value: formatCurrency(data.totalWinAmountToday),
          sub: '',
          icon: TrendingDown,
        },
        {
          label: t.dashboard.houseEdge,
          value: formatCurrency(houseEdge),
          sub: '',
          icon: TrendingUp,
          accent: parseFloat(houseEdge) >= 0 ? ('success' as const) : ('destructive' as const),
        },
        {
          label: t.dashboard.openRounds,
          value: data.openRounds.toLocaleString(),
          sub: '',
          icon: Circle,
          accent: data.openRounds > 0 ? ('success' as const) : undefined,
        },
      ]
    : []

  return (
    <div className="p-4 md:p-6 max-w-4xl">
      <div className="flex items-center justify-between mb-5">
        <span className="font-mono text-xs font-bold uppercase tracking-wider text-foreground">
          {t.dashboard.title}
        </span>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => void load()}
          disabled={loading}
          className="h-7 gap-1.5 font-mono text-xs text-muted-foreground"
        >
          <RefreshCw className={cn('h-3 w-3', loading && 'animate-spin')} />
          {t.common.refresh}
        </Button>
      </div>

      {data?.maintenanceMode && (
        <div className="flex items-center gap-2 p-3 border border-destructive/40 rounded-[var(--radius)] bg-destructive/5 mb-4">
          <Wrench className="h-4 w-4 text-destructive shrink-0" />
          <p className="font-mono text-xs text-destructive font-semibold">{t.dashboard.maintenanceActive}</p>
        </div>
      )}

      {error && (
        <div
          className="flex items-center gap-2 p-3 border border-destructive/40 rounded-[var(--radius)] bg-card mb-4"
          role="alert"
        >
          <AlertCircle className="h-4 w-4 text-destructive shrink-0" />
          <p className="font-mono text-xs text-destructive">{error}</p>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {loading
          ? Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="border border-border rounded-[var(--radius)] bg-card p-4">
                <Skeleton className="h-2.5 w-20 mb-3" />
                <Skeleton className="h-7 w-28 mb-1.5" />
                <Skeleton className="h-2 w-16" />
              </div>
            ))
          : cards.map((card) => {
              const Icon = card.icon
              return (
                <div key={card.label} className="border border-border rounded-[var(--radius)] bg-card p-4">
                  <div className="flex items-center gap-1.5 mb-2">
                    <Icon className="h-3 w-3 text-muted-foreground" aria-hidden />
                    <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                      {card.label}
                    </span>
                  </div>
                  <p
                    className={cn(
                      'font-mono text-2xl font-bold',
                      card.accent === 'success'
                        ? 'text-success'
                        : card.accent === 'destructive'
                          ? 'text-destructive'
                          : 'text-foreground',
                    )}
                  >
                    {card.value}
                  </p>
                  <p className="font-mono text-[10px] text-muted-foreground/60 mt-1">{card.sub}</p>
                </div>
              )
            })}
      </div>
    </div>
  )
}
